import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { tvShows, articles } from '@/mocks/tv-data';
import type { TvShow } from '@/mocks/tv-data';

function ShowDetail({ show }: { show: TvShow }) {
  const genreLabels: Record<string, string> = {
    costume: '古装',
    suspense: '悬疑',
    romance: '爱情',
    action: '动作',
    comedy: '喜剧',
    urban: '都市',
    fantasy: '奇幻',
  };

  const regionLabels: Record<string, string> = {
    mainland: '中国大陆',
    'hk-tw': '港台',
    korea: '韩国',
    japan: '日本',
    western: '欧美',
  };

  const categoryLabels: Record<string, string> = {
    drama: '电视剧',
    movie: '电影',
    anime: '动漫',
    variety: '综艺',
  };

  const related = tvShows
    .filter((s) => s.id !== show.id && s.genre.some((g) => show.genre.includes(g)))
    .slice(0, 6);

  return (
    <>
      <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <img
          src={show.bannerImage}
          alt={show.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 -mt-32 relative z-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
            <div className="aspect-[3/4] rounded-xl overflow-hidden border-2 border-background-200/60">
              <img
                src={show.coverImage}
                alt={show.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 pt-4 md:pt-8">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {show.isNew && (
                <span className="px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-bold whitespace-nowrap">
                  NEW
                </span>
              )}
              {show.isHot && (
                <span className="px-2.5 py-1 rounded-full bg-primary-500 text-white text-xs font-bold whitespace-nowrap">
                  HOT
                </span>
              )}
              <span className="px-2.5 py-1 rounded-full bg-primary-500/10 border border-primary-500/30 text-primary-400 text-xs font-medium whitespace-nowrap">
                {show.status}
              </span>
              <span className="text-sm text-foreground-400">{categoryLabels[show.category] || show.category}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 mb-1">
              {show.title}
            </h1>
            <p className="text-sm text-foreground-500 mb-4">{show.englishTitle}</p>

            <div className="flex items-center gap-4 mb-4 flex-wrap text-sm text-foreground-400">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-primary-500 text-sm"></i>
                </div>
                <span className="text-primary-400 font-semibold text-lg">{show.rating}</span>
              </div>
              <span>{show.year}</span>
              <span>{regionLabels[show.region] || show.region}</span>
              <span>导演: {show.director}</span>
              <span>{show.episodeCount}集全</span>
            </div>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {show.genre.map((g) => (
                <Link
                  key={g}
                  to={`/category/all`}
                  className="px-3 py-1 rounded-full bg-background-200/70 border border-background-300/60 text-xs text-foreground-400 hover:text-primary-500 hover:border-primary-500/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {genreLabels[g] || g}
                </Link>
              ))}
            </div>

            <div className="bg-background-100 rounded-xl border border-background-200/60 p-4 md:p-5 mb-5">
              <h2 className="text-sm font-semibold text-foreground-200 mb-2">剧情简介</h2>
              <p className="text-sm text-foreground-400 leading-relaxed">{show.description}</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to="/category/all"
                className="px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                立即播放
              </Link>
              <button className="px-6 py-2.5 rounded-full bg-background-200/70 border border-background-300/60 text-foreground-300 hover:text-foreground-100 transition-colors cursor-pointer whitespace-nowrap text-sm">
                收藏
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-background-100 rounded-xl border border-background-200/60 p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground-200 mb-3">主演阵容</h3>
            <div className="flex items-center gap-3 flex-wrap">
              {show.cast.map((actor) => (
                <span
                  key={actor}
                  className="px-3 py-1.5 rounded-full bg-background-200/50 text-xs text-foreground-400 whitespace-nowrap"
                >
                  {actor}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-background-100 rounded-xl border border-background-200/60 p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground-200 mb-3">更新状态</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">总集数</span>
                <span className="text-foreground-300 font-medium">{show.episodeCount}集</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">已更新</span>
                <span className="text-foreground-300 font-medium">{show.currentEpisode}集</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">状态</span>
                <span className="text-primary-400 font-medium">{show.status}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">更新日</span>
                <span className="text-foreground-300">{show.updateDay}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-500">播放量</span>
                <span className="text-foreground-300">{(show.views / 10000).toFixed(0)}万次</span>
              </div>
            </div>
          </div>
        </div>

        {show.episodeCount > 1 && (
          <div className="mt-10">
            <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">剧集列表</h2>
            <div className="bg-background-100 rounded-xl border border-background-200/60 p-4 md:p-5">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {Array.from({ length: show.episodeCount }, (_, i) => i + 1).map((ep) => (
                  <button
                    key={ep}
                    className={`py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      ep <= show.currentEpisode
                        ? 'bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 border border-primary-500/20'
                        : 'bg-background-200/50 text-foreground-600 border border-background-200/60'
                    }`}
                  >
                    第{ep}集
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-10 pb-14">
            <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group cursor-pointer block"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                    <img
                      src={rel.coverImage}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-primary-400 text-xs font-semibold">
                      {rel.rating}
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                    {rel.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1">{rel.cast.slice(0, 3).join(' / ')}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: show.title,
            description: show.description,
            image: show.coverImage,
            director: {
              '@type': 'Person',
              name: show.director,
            },
            actor: show.cast.map((name) => ({
              '@type': 'Person',
              name: name,
            })),
            dateCreated: `${show.year}`,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: show.rating,
              bestRating: '10',
            },
          }),
        }}
      />
    </>
  );
}

function ArticleDetail() {
  const article = articles[0];

  return (
    <>
      <div className="relative w-full h-[250px] md:h-[320px] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background-50"></div>
      </div>

      <div className="max-w-[900px] mx-auto px-4 md:px-6 -mt-20 relative z-10">
        <div className="bg-background-100 rounded-2xl border border-background-200/60 p-6 md:p-8 mb-10">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium whitespace-nowrap">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.publishDate}</span>
            <span className="text-xs text-foreground-500">作者: {article.author}</span>
            <span className="text-xs text-foreground-500">阅读 {(article.views / 1000).toFixed(0)}k</span>
          </div>

          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-5">
            {article.title}
          </h1>

          <div className="text-sm text-foreground-400 leading-relaxed space-y-4">
            {article.content.split('\n\n').map((paragraph, i) => (
              <p key={i}>{paragraph.trim()}</p>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-6 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-background-200/50 text-xs text-foreground-500 whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        <div className="pb-14">
          <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">相关文章</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {articles.filter((a) => a.id !== article.id).slice(0, 2).map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group cursor-pointer bg-background-100 rounded-xl border border-background-200/60 overflow-hidden hover:border-primary-500/30 transition-all flex flex-col sm:flex-row"
              >
                <div className="w-full sm:w-40 flex-shrink-0 aspect-[16/10] sm:aspect-auto overflow-hidden">
                  <img
                    src={a.coverImage}
                    alt={a.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 flex-1">
                  <span className="px-2 py-0.5 rounded-md bg-primary-500/10 text-primary-400 text-[10px] font-medium whitespace-nowrap mb-2 inline-block">
                    {a.category}
                  </span>
                  <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                    {a.title}
                  </h3>
                  <p className="text-xs text-foreground-500">{a.publishDate}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            image: article.coverImage,
          }),
        }}
      />
    </>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const showId = parseInt(id || '0');

  const show = tvShows.find((s) => s.id === showId);
  const article = articles.find((a) => a.id === showId);

  if (show) {
    const categoryLabels: Record<string, string> = {
      drama: '电视剧',
      movie: '电影',
      anime: '动漫',
      variety: '综艺',
    };
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabels[show.category] || show.category, href: `/category/${show.category}` },
                { label: show.title },
              ]}
            />
          </div>
          <ShowDetail show={show} />
        </main>
        <Footer />
      </div>
    );
  }

  if (article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-3">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category },
                { label: article.title },
              ]}
            />
          </div>
          <ArticleDetail />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-600"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-200 mb-2">内容未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您要查看的内容不存在或已被删除</p>
          <Link
            to="/"
            className="px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors inline-block cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}